/** The tools at the right end of a power table's filter line: Group, Density, Columns, Export and the
 * shortcut sheet.
 *
 * Each is a label and a menu or a small panel; none carries a sentence except Export, whose one line
 * states what the file will contain ("214 rows, 9 columns, filtered by Scope: 2") so nobody downloads
 * a file they have to open to understand.
 */

import { Columns, DownloadSimple, Keyboard, Rows, X } from "@phosphor-icons/react";

import { InfoPopover } from "../InfoPopover";
import { DENSITIES, DENSITY_HEIGHT } from "./gridTypes";
import type { GridDensity } from "./gridTypes";
import { PopMenu } from "./PopMenu";
import styles from "./tablePower.module.css";
import type { GridPower } from "./useGridPower";

const DENSITY_WORD: Readonly<Record<GridDensity, string>> = {
  compact: "Compact",
  comfortable: "Comfortable",
  spacious: "Spacious",
};

export function DensityMenu({ power }: { power: GridPower }) {
  return (
    <PopMenu
      label="Row density"
      testId="density-menu"
      trigger={
        <>
          <Rows size={14} aria-hidden="true" />
          {DENSITY_WORD[power.density]}
        </>
      }
    >
      {(close) =>
        DENSITIES.map((density) => (
          <button
            key={density}
            type="button"
            role="menuitemradio"
            aria-checked={power.density === density}
            className={styles.item}
            data-testid={`density-${density}`}
            onClick={() => {
              power.setDensity(density);
              close();
            }}
          >
            <span className={styles.itemLabel}>{DENSITY_WORD[density]}</span>
            <span className={styles.itemHint}>{DENSITY_HEIGHT[density]} px</span>
          </button>
        ))
      }
    </PopMenu>
  );
}

export function ColumnChooser({ power }: { power: GridPower }) {
  const hiddenCount = power.hidden.size;
  return (
    <PopMenu
      kind="panel"
      label="Choose columns"
      testId="column-chooser"
      trigger={
        <>
          <Columns size={14} aria-hidden="true" />
          Columns
          {hiddenCount > 0 ? (
            <span className={styles.badge} data-testid="hidden-count">
              {hiddenCount}
              <span className="visuallyHidden"> hidden</span>
            </span>
          ) : null}
        </>
      }
    >
      {() => (
        <div className={styles.chooser}>
          {power.columnsMeta.map((column) => (
            <label key={column.id} className={styles.choice}>
              <input
                type="checkbox"
                checked={!power.hidden.has(column.id)}
                disabled={!column.hideable}
                onChange={() => power.toggleHidden(column.id)}
              />
              <span>{column.header}</span>
            </label>
          ))}
          {hiddenCount > 0 ? (
            <button type="button" className={styles.linkButton} onClick={power.showAllColumns}>
              Show all columns
            </button>
          ) : null}
        </div>
      )}
    </PopMenu>
  );
}

export function ExportMenu({ power }: { power: GridPower }) {
  return (
    <PopMenu
      kind="panel"
      align="right"
      label="Export"
      testId="export-menu"
      trigger={
        <>
          <DownloadSimple size={14} aria-hidden="true" />
          Export
        </>
      }
    >
      {(close) => (
        <div className={styles.chooser}>
          <p className={styles.exportLine} data-testid="export-summary">
            {power.exportSummary(false) || "Nothing to export"}
          </p>
          <button
            type="button"
            className={styles.primary}
            data-testid="export-csv"
            onClick={() => {
              power.exportCsv(false);
              close();
            }}
          >
            CSV
          </button>
        </div>
      )}
    </PopMenu>
  );
}

/** "Group: Site" with its remove button, and the note on what a subtotal is behind an info control. */
export function GroupChip({ power }: { power: GridPower }) {
  const id = power.groupBy;
  if (id === null) return null;
  const header = power.columnsMeta.find((c) => c.id === id)?.header ?? id;
  return (
    <span className={styles.chipOn} data-testid="group-chip">
      <span className={styles.chipMain}>{`Group: ${header}`}</span>
      <button
        type="button"
        className={styles.chipRemove}
        aria-label={`Remove grouping by ${header}`}
        onClick={() => power.setGroupBy(null)}
      >
        <X size={11} weight="bold" aria-hidden="true" />
      </button>
      <InfoPopover infoKey="shell.table-subtotals" />
    </span>
  );
}

export function TableTools({ power }: { power: GridPower }) {
  return (
    <span className={styles.tools} data-testid="table-tools">
      <GroupChip power={power} />
      <DensityMenu power={power} />
      <ColumnChooser power={power} />
      <ExportMenu power={power} />
      <button
        type="button"
        className={styles.iconTool}
        aria-label="Keyboard shortcuts"
        data-testid="shortcuts-open"
        onClick={() => power.setShortcutsOpen(true)}
      >
        <Keyboard size={15} aria-hidden="true" />
      </button>
    </span>
  );
}
